import * as React from 'react';
import { useMemo, useState } from 'react';
import { toast } from 'react-toastify';

import { Fulfillment, FulfillmentProduct } from './types';
import { axios } from '../api';
import { Spinner } from '../assets/Spinner';
import { CheckboxTable } from '../component/CheckboxTable';
import { ConfirmationModal } from '../component/ConfirmationModal';
import { Button } from '../component/buttons/Button';
import { useGetCustomer } from '../hooks/getCustomer';
import { useCustomParams } from '../hooks/useCustomParams';
import { SplitFulfillmentHeaders } from '../tableHeaders';
import { BOTTOM_TOAST, generateFullName, getCountryFlag, getOrder } from '../utils';

export const SplitFulfillment = () => {
  const { customerId, orderId, fulfillmentId } = useCustomParams();
  const { customer } = useGetCustomer(customerId);
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [isProcessLoading, setIsProcessLoading] = useState(false);

  const order = useMemo(() => {
    return getOrder(customer?.orders, orderId);
  }, [customer?.orders, orderId]);

  const fulfillment = useMemo(() => {
    return order?.fulfillments.find((f: Fulfillment) => f.id === fulfillmentId);
  }, [order?.fulfillments, fulfillmentId]);

  const handleCheckboxChange = (index: number) => {
    if (selectedRows.includes(index)) {
      setSelectedRows((prevSelected) => prevSelected.filter((rowIndex) => rowIndex !== index));
    } else {
      setSelectedRows((prevSelected) => [...prevSelected, index]);
    }
  };

  const getPetName = (petId: string) => {
    const pet = customer?.pets.find((pet) => pet.id === petId);
    return pet ? pet.name : '--';
  };

  const formattedFulfillmentProducts = (fulfillmentProducts: FulfillmentProduct[]) => {
    return fulfillmentProducts.map((product: FulfillmentProduct) => ({
      'Fulfillment ID': fulfillmentId,
      'Product Description': product.description ?? '--',
      'Pet Name': getPetName(product.pet_id),
      Quantity: product.quantity,
      'Product Code': product.code,
    }));
  };

  const handleSplitFulfillment = () => {
    setIsProcessLoading(true);

    const mappedFulfillments = formattedFulfillmentProducts(fulfillment.products);
    const productCodesToSplit = selectedRows.map((index) => {
      const orderProduct = mappedFulfillments[index];
      return orderProduct['Product Code'];
    });

    axios
      .post(`fulfillments/${fulfillmentId}/split`, {
        product_codes_to_split: productCodesToSplit,
      })
      .then(() => {
        setIsProcessLoading(false);
        setShowModal(false);

        // Save toast message in localStorage
        localStorage.setItem(
          'toastMessage',
          JSON.stringify({
            message: fulfillmentId,
            type: 'success', // Optional: Toast type
          })
        );

        // Navigate to the new page
        const urlParts = window.location.href.split('/');
        urlParts.pop();
        urlParts.pop();
        location.href = urlParts.join('/');
      })
      .catch(() => {
        toast.error(
          'Error splitting fulfillment ' +
            fulfillmentId +
            '. Please ensure fulfillment is not already split off from another, and that more than one product exists in this fulfillment.',
          BOTTOM_TOAST
        );
        setIsProcessLoading(false);
        setShowModal(false);
      });
  };

  if (!customer) return <Spinner />;

  return (
    <div className="h-[calc(100vh)] bg-slate50 p-8">
      <div className="pt-3 pb-3">
        <h2 className="mb-1 flex items-center">Split Fulfillment</h2>
        <b>Customer:</b>{' '}
        {`${getCountryFlag(customer.default_country)} ${generateFullName(
          customer.first_name,
          customer.last_name
        )} (${customer.email})`}{' '}
        <br />
        <b>Order:</b> {order.id}
      </div>
      <div className="bg-white">
        <b className="mb-4 flex">
          Select the product(s) you would like to split into a separate fulfillment:
        </b>{' '}
        <CheckboxTable
          header={SplitFulfillmentHeaders}
          data={formattedFulfillmentProducts(fulfillment.products)}
          selectedRows={selectedRows}
          onCheckboxChange={handleCheckboxChange}
        />
      </div>

      <div className="flex items-center justify-center pt-5 pb-4">
        <Button
          label="Split Fulfillment"
          onClick={() => {
            setShowModal(true);
          }}
          isDisabled={selectedRows.length === 0}
          variant={selectedRows.length > 0 ? 'primary' : 'secondary'}
        />
        <br />
      </div>
      {showModal && (
        <ConfirmationModal
          isProcessLoading={isProcessLoading}
          title="Are you sure you want to proceed with splitting this fulfillment?"
          message="A new fulfillment will be created in our database with the selected products. Please make sure to make this same split in Order Desk afterwards."
          confirmLabel="Confirm"
          onConfirm={handleSplitFulfillment}
          onCancel={() => setShowModal(false)}
        />
      )}
    </div>
  );
};
